import { useCallback, useEffect, useMemo, useState } from "react";

import {
  selectedZoneVariable,
  useSelectedZoneVariable,
} from "../../pages/data-hub/components/liveview/components/devices/ptzonelist/zones.variable";
import { useListenToDeviceDataChange } from "../../pages/deployment-health/hooks/useListenToDeviceDataChange";
import { DeviceDataType } from "../../pages/devices/hooks/useDeviceTableRows";
import { handleApolloError } from "../../common/utils/handleApolloError";
import { usePublishNode } from "../../pages/devices/hooks/usePublishNode";
import { useZoneFormValidation } from "../../pages/devices/zones-setup-page/hooks/useZoneFormValidation";
import type { CurrentPosition } from "../../API";
import { extractDeviceNameFromId } from "../utils/getEntityName";
import { selectedKeyframeVariable } from "../variables/selectedKeyframe";

export const PAN_MIN = -180;

export const PAN_MAX = 180;

export const TILT_MIN = -90;

export const TILT_MAX = 35;

export const ZOOM_MIN = 0;

export const ZOOM_MAX = 100;

export const PAN_TILT_STEP = 1;

export const PAN_DEFAULT = "0";

export const TILT_DEFAULT = "0";

export const ZOOM_DEFAULT = "0";

export type GoToPTHandlerType = (
  pan: string,
  tilt: string,
  zoom: string,
  selectedZoneName?: string
) => void;

interface IUsePTController {
  deviceId: string;
  deviceName: string;
  nodeId: string;
  deviceData?: DeviceDataType;
  currentPosition?: CurrentPosition | null;
  zoneName?: string;
}

export const usePTController = ({
  deviceId,
  deviceName,
  nodeId,
  deviceData,
  currentPosition,
  zoneName,
}: IUsePTController) => {
  const selectedZone = useSelectedZoneVariable();

  const {
    validatePanTilt,
    validationState,
    setValidationState,
    validateZoom,
    resetValidationState,
    validateCreateZoneForm,
  } = useZoneFormValidation(zoneName ?? selectedZone?.zoneName);

  const { publishNode } = usePublishNode();

  const { extractedDeviceName } = extractDeviceNameFromId(deviceId);

  const { data, currentPosition: subscribedCurrentPosition } =
    useListenToDeviceDataChange({
      currentDeviceId: deviceId,
      onError: handleApolloError,
    });

  const [pan, setPan] = useState(PAN_DEFAULT);
  const [tilt, setTilt] = useState(TILT_DEFAULT);
  const [zoom, setZoom] = useState(ZOOM_DEFAULT);

  useEffect(() => {
    if (deviceData || currentPosition) {
      const panValue = currentPosition?.pan
        ? `${currentPosition.pan}`
        : PAN_DEFAULT;

      const tiltValue = currentPosition?.tilt
        ? `${currentPosition.tilt}`
        : TILT_DEFAULT;

      const zoomValue = currentPosition?.zoom
        ? `${currentPosition.zoom}`
        : ZOOM_DEFAULT;

      setPan(panValue);

      setTilt(tiltValue);

      setZoom(zoomValue);

      selectedZoneVariable({
        zoneName: "",
        pan: panValue,
        tilt: tiltValue,
        zoom: zoomValue,
      });
    }
  }, []);

  useEffect(() => {
    if (
      subscribedCurrentPosition &&
      data?.listenToDeviceData?.name === deviceName
    ) {
      const panValue = subscribedCurrentPosition.pan
        ? `${subscribedCurrentPosition.pan}`
        : PAN_DEFAULT;

      const tiltValue = subscribedCurrentPosition.tilt
        ? `${subscribedCurrentPosition.tilt}`
        : TILT_DEFAULT;

      const zoomValue = subscribedCurrentPosition.zoom
        ? `${subscribedCurrentPosition.zoom}`
        : ZOOM_DEFAULT;

      selectedZoneVariable({
        zoneName: "",
        pan: panValue,
        tilt: tiltValue,
        zoom: zoomValue,
      });
    }
  }, [subscribedCurrentPosition, data?.listenToDeviceData?.name, deviceName]);

  const handleGoToPt = useCallback(
    (
      panPosition: string,
      tiltPosition: string,
      zoomValue: string,
      selectedZoneName: string = "",
      disablePTMovement: boolean = false
    ) => {
      selectedKeyframeVariable("");

      const isValid = validatePanTilt(panPosition, tiltPosition);

      const hasZoom = deviceData?.hasZoom;

      const isValidZoom = hasZoom ? validateZoom(zoomValue) : true;

      if (isValid && isValidZoom) {
        setPan(panPosition);

        setTilt(tiltPosition);

        setZoom(zoomValue);

        selectedZoneVariable({
          zoneName: selectedZoneName,
          pan: panPosition,
          tilt: tiltPosition,
          zoom: zoomValue,
        });

        if (disablePTMovement) {
          return;
        }

        publishNode({
          message: JSON.stringify({
            TARGET: "PANTILT",
            ACTION: "GOTO_PT",
            device_name: extractedDeviceName,
            pan: panPosition,
            tilt: tiltPosition,
            zoom: zoomValue,
          }),
          nodeId,
        });
      }
    },
    [
      validatePanTilt,
      validateZoom,
      deviceData,
      extractedDeviceName,
      nodeId,
      publishNode,
    ]
  );

  const adjustByStep = useMemo(() => {
    return {
      setHorizontalUp(): void {
        let newPan = `${+pan + PAN_TILT_STEP}`;

        // MOVE_PT_RIGHT
        if (+pan >= PAN_MAX) {
          newPan = `${PAN_MAX}`;
        }

        if (+newPan === +pan) return;

        handleGoToPt(newPan, tilt, zoom, "", true);
      },

      setHorizontalDown(): void {
        let newPan = `${+pan - PAN_TILT_STEP}`;

        // MOVE_PT_LEFT
        if (+pan <= PAN_MIN) {
          newPan = `${PAN_MIN}`;
        }

        if (+newPan === +pan) return;

        handleGoToPt(newPan, tilt, zoom, "", true);
      },

      setVerticalUp(): void {
        let newTilt = `${+tilt + PAN_TILT_STEP}`;

        // MOVE_PT_UP
        if (+newTilt >= TILT_MAX) {
          newTilt = `${TILT_MAX}`;
        }

        if (+newTilt === +tilt) return;

        handleGoToPt(pan, newTilt, zoom, "", true);
      },

      setVerticalDown(): void {
        let newTilt = `${+tilt - PAN_TILT_STEP}`;

        if (+tilt <= TILT_MIN) {
          newTilt = `${TILT_MIN}`;
        }

        if (+newTilt === +tilt) return;

        handleGoToPt(pan, newTilt, zoom, "", true);
      },
    };
  }, [pan, tilt, zoom, handleGoToPt]);

  const movePtUp = useCallback(() => {
    adjustByStep.setVerticalUp();
  }, [adjustByStep]);

  const movePtDown = useCallback(() => {
    adjustByStep.setVerticalDown();
  }, [adjustByStep]);

  const movePtLeft = useCallback(() => {
    adjustByStep.setHorizontalDown();
  }, [adjustByStep]);

  const movePtRight = useCallback(() => {
    adjustByStep.setHorizontalUp();
  }, [adjustByStep]);

  const handlePanInputChange = useCallback(
    (pan = "") => {
      selectedKeyframeVariable("");

      setValidationState(prev => ({
        ...prev,
        pan: {
          hasError: false,
          errorMessage: "",
        },
        panValue: {
          hasError: false,
          errorMessage: "",
        },
      }));

      setPan(pan);
    },
    [setPan, setValidationState]
  );

  const handleTiltInputChange = useCallback(
    (tilt = "") => {
      selectedKeyframeVariable("");

      setValidationState(prev => ({
        ...prev,
        tilt: {
          hasError: false,
          errorMessage: "",
        },
        tiltValue: {
          hasError: false,
          errorMessage: "",
        },
      }));

      setTilt(tilt);
    },
    [setTilt, setValidationState]
  );

  const handleZoomInputChange = useCallback(
    (zoom = "") => {
      selectedKeyframeVariable("");

      setValidationState(prev => ({
        ...prev,
        zoom: {
          hasError: false,
          errorMessage: "",
        },
        zoomValue: {
          hasError: false,
          errorMessage: "",
        },
      }));

      setZoom(zoom);
    },
    [setZoom, setValidationState]
  );

  return {
    pan,
    tilt,
    zoom,
    disabled: false,
    adjustByStep,
    movePtUp,
    movePtDown,
    movePtLeft,
    movePtRight,
    handleGoToPt,
    handlePanInputChange,
    handleTiltInputChange,
    handleZoomInputChange,
    validationState,
    setValidationState,
    resetValidationState,
    validateCreateZoneForm,
  };
};
